<template>
  <div class="container mt-5">
    <div class="row justify-content-center">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header">Register</div>
          <div class="card-body">
            <form @submit.prevent="register">
              <div class="mb-3">
                <label for="displayName" class="form-label">Display Name</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="displayName" 
                  v-model="displayName" 
                  required
                >
              </div>
              <div class="mb-3">
                <label for="email" class="form-label">Email address</label>
                <input 
                  type="email" 
                  class="form-control" 
                  id="email" 
                  v-model="email" 
                  required
                >
              </div>
              <div class="mb-3">
                <label for="password" class="form-label">Password</label>
                <input 
                  type="password" 
                  class="form-control" 
                  id="password" 
                  v-model="password" 
                  required
                  minlength="6"
                >
                <small class="form-text text-muted">Password must be at least 6 characters long</small>
              </div>
              <div class="mb-3">
                <label for="confirmPassword" class="form-label">Confirm Password</label>
                <input 
                  type="password" 
                  class="form-control" 
                  id="confirmPassword" 
                  v-model="confirmPassword" 
                  required
                >
              </div>
              <div class="d-grid gap-2">
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                  {{ isLoading ? 'Registering...' : 'Register' }}
                </button>
                <button type="button" class="btn btn-secondary" @click="registerWithGoogle">
                  Register with Google
                </button>
              </div>
              <div v-if="error" class="alert alert-danger mt-3">
                {{ error }}
              </div>
            </form>
            <div class="text-center mt-3">
              <p>Already have an account? <router-link to="/login">Login here</router-link></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { 
  createUserWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider,
  updateProfile 
} from 'firebase/auth';
import { auth } from '@/firebase';

export default {
  name: 'RegisterPage',
  setup() {
    const router = useRouter();
    const route = useRoute();

    // A player following a shared link is more likely to register than log in,
    // so registration has to carry the destination too. Same-site paths only.
    const afterAuth = () => {
      const target = route.query.redirect;
      const safe = typeof target === 'string' && target.startsWith('/') && !target.startsWith('//');
      router.push(safe ? target : '/');
    };
    const displayName = ref('');
    const email = ref('');
    const password = ref('');
    const confirmPassword = ref('');
    const error = ref(null);
    const isLoading = ref(false);

    const register = async () => {
      error.value = null;
      
      // Validate inputs
      if (password.value !== confirmPassword.value) {
        error.value = 'Passwords do not match';
        return;
      }

      if (password.value.length < 6) {
        error.value = 'Password must be at least 6 characters long';
        return;
      }

      isLoading.value = true;
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          email.value, 
          password.value
        );

        // Update user profile with display name
        await updateProfile(userCredential.user, {
          displayName: displayName.value
        });

        afterAuth();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };

    const registerWithGoogle = async () => {
      error.value = null;
      isLoading.value = true;
      try {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
        afterAuth();
      } catch (err) {
        error.value = err.message;
      } finally {
        isLoading.value = false;
      }
    };

    return {
      displayName,
      email,
      password,
      confirmPassword,
      error,
      isLoading,
      register,
      registerWithGoogle
    };
  }
}
</script>

<style scoped>
.card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
</style>